import { ListingTile } from '@nocowanie/noc-api';

import { dateHelpers, formatHelpers } from '@nocowanie/core/helpers';
import { wordPluralisation } from '@nocowanie/common-ui/helpers';

import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile';
import { SearchCriteria } from '@app/models';
import { Currency } from '@app/models/api/api-types';

import s from './accommodation-tile-lite.module.css';

interface TilePriceColumnProps {
    tile: ListingTile;
    searchCriteria: Partial<SearchCriteria>;
    withDates: boolean;
    nightsCount: number;
    t: AccommodationTileProps['t'];
}

/** Same text as `GuestsNightsLabel` with `withListingTile` (e.g. "za 3 noce, 3 osoby") */
const getGuestsNightsLabel = (
    adultsCount: number,
    childrenCount: number,
    nights: number,
    t: AccommodationTileProps['t'],
) => {
    const nightForms = t('guestsNightsLabel.night', { returnObjects: true }) as Record<
        string,
        string
    >;
    const personForms = t('guestsNightsLabel.person', { returnObjects: true }) as Record<
        string,
        string
    >;
    const totalGuests = `${adultsCount}${childrenCount ? ` + ${childrenCount}` : ''}`;
    const guestsToPluralise = childrenCount > 0 ? childrenCount : adultsCount;
    const nightsLabel = `${nights} ${wordPluralisation(
        nights,
        nightForms.singular,
        nightForms.plural,
        nightForms.genitivePlural,
    )}`;
    const guestsLabel = `${totalGuests} ${wordPluralisation(
        guestsToPluralise,
        personForms.singular,
        personForms.plural,
        personForms.genitivePlural,
    )}`;

    return `za ${nightsLabel}, ${guestsLabel}`;
};

/** Same discount logic and markup as `Price` (accommodationTile variant), without Chakra runtime styling */
const getDiscount = (price: number, basePrice: number, discountValue?: number) => {
    if (!basePrice || !price || basePrice === price) {
        return undefined;
    }

    return Math.round(discountValue ?? ((basePrice - price) / basePrice) * 100).toFixed(0);
};

export const TilePriceColumn = ({
    tile,
    searchCriteria,
    withDates,
    nightsCount,
    t,
}: TilePriceColumnProps) => {
    const isInquiry = tile.source === ListingTile.source.NOC;
    const { rate } = tile;
    const withFlexDates = !!searchCriteria.flexDates;

    const currency = (rate?.convertedCurrency ?? Currency.Pln) as Currency;
    const price = rate?.convertedRate ?? 0;
    // Do not display discount info when dates are not set
    const basePrice = (!withDates ? rate?.convertedRate : rate?.convertedBaseRate) ?? 0;
    const discount = getDiscount(price, basePrice, withDates ? rate?.discount : undefined);

    return (
        <div className={isInquiry ? s.priceColumnInquiry : s.priceColumn}>
            {isInquiry ? (
                <p data-testid={'acc-tile-price-confirmation'} className={s.priceConfirmation}>
                    {t('priceConfirmationInfo')}
                </p>
            ) : (
                <>
                    {!!Number(searchCriteria.flexDates) && rate?.checkIn && rate?.checkOut ? (
                        <div className={s.flexDatesBadge}>
                            <span className={s.flexDatesBadgeLabel}>
                                {dateHelpers.formatDateRange(rate.checkIn, rate.checkOut)}
                            </span>
                        </div>
                    ) : null}
                    {rate ? (
                        <div className={s.price} data-testid={'price'}>
                            {discount ? (
                                <div
                                    data-testid={'price-discount'}
                                    className={
                                        withFlexDates ? s.discountBadgeFlex : s.discountBadge
                                    }
                                >
                                    <span className={s.discountBadgeLabel}>{`-${discount}%`}</span>
                                </div>
                            ) : null}
                            <div className={s.priceValueWrapper}>
                                {discount ? (
                                    <del data-testid={'price-base'} className={s.priceBase}>
                                        {formatHelpers.price(basePrice, currency)}
                                    </del>
                                ) : null}
                                <strong
                                    data-testid={'price-final'}
                                    className={discount ? s.priceFinalDiscounted : s.priceFinal}
                                >
                                    {!withDates ? (
                                        <span className={s.priceFrom}>{t('priceFrom')}</span>
                                    ) : null}
                                    {formatHelpers.price(price, currency)}
                                </strong>
                            </div>
                            <span className={s.guestsNights}>
                                {getGuestsNightsLabel(
                                    searchCriteria.guests?.adultsCount ?? 2,
                                    searchCriteria.guests?.childrenCount ?? 0,
                                    nightsCount,
                                    t,
                                )}
                            </span>
                        </div>
                    ) : null}
                </>
            )}

            <button type={'button'} className={s.cta} data-testid={'acc-tile-cta'}>
                {t(isInquiry ? 'ctaInquiry' : 'ctaOTA')}
            </button>
        </div>
    );
};
